import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ChatConfigManager.css';

const ChatConfigManager = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [config, setConfig] = useState({
    customPrompt: '',
    suggestedQuestions: ['', '', '', '']
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // API Key state
  const [apiKey, setApiKey] = useState('');
  const [hasKey, setHasKey] = useState(false);
  const [showKeyInput, setShowKeyInput] = useState(false);

  useEffect(() => {
    loadCategories();
    loadApiKeyStatus();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      loadConfig();
    }
  }, [selectedCategory]);

  const loadApiKeyStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ai/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasKey(response.data.hasKey);
      if (response.data.apiKey) {
        setApiKey(response.data.apiKey);
      }
    } catch (error) {
      console.error('Error loading API key status:', error);
    }
  };

  const saveApiKey = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/ai/settings', { apiKey }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasKey(true);
      setShowKeyInput(false);
      setMessage({ type: 'success', text: '✅ Το API Key αποθηκεύτηκε!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Σφάλμα: ' + error.message });
    }
  };

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
      if (response.data.length > 0) {
        setSelectedCategory(response.data[0].name);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/ai/config/${selectedCategory}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const loadedConfig = response.data;
      setConfig({
        customPrompt: loadedConfig.customPrompt || '',
        suggestedQuestions: loadedConfig.suggestedQuestions?.length > 0
          ? loadedConfig.suggestedQuestions
          : ['', '', '', '']
      });
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      
      // Filter out empty questions
      const filteredQuestions = config.suggestedQuestions.filter(q => q.trim() !== '');
      
      await axios.put(
        `/api/ai/config/${selectedCategory}`,
        {
          customPrompt: config.customPrompt,
          suggestedQuestions: filteredQuestions
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage({
        type: 'success',
        text: '✅ Οι ρυθμίσεις AI αποθηκεύτηκαν επιτυχώς!'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: '❌ Σφάλμα κατά την αποθήκευση: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuestion = (index, value) => {
    const newQuestions = [...config.suggestedQuestions];
    newQuestions[index] = value;
    setConfig({ ...config, suggestedQuestions: newQuestions });
  };

  const addQuestion = () => {
    setConfig({
      ...config,
      suggestedQuestions: [...config.suggestedQuestions, '']
    });
  };

  const removeQuestion = (index) => {
    const newQuestions = config.suggestedQuestions.filter((_, i) => i !== index);
    setConfig({ ...config, suggestedQuestions: newQuestions });
  };

  return (
    <div className="chat-config-manager">
      <div className="chat-config-header">
        <h2>🤖 Ρυθμίσεις AI Chatbot</h2>
        <p>Προσαρμόστε τις απαντήσεις του AI ανά κατηγορία υπηρεσιών</p>
      </div>

      <div className="config-section api-key-section">
        <h3>🔑 OpenAI API Key</h3>
        <div className="api-key-status">
          Status: {hasKey ? <span className="status-active">✅ Ενεργό</span> : <span className="status-inactive">❌ Μη ορισμένο</span>}
        </div>
        
        {!showKeyInput ? (
          <button onClick={() => setShowKeyInput(true)} className="edit-key-btn">
            {hasKey ? 'Αλλαγή API Key' : 'Ορισμός API Key'}
          </button>
        ) : (
          <div className="api-key-input-group">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
              className="api-key-input"
            />
            <button onClick={saveApiKey} className="save-key-btn">Αποθήκευση Key</button>
            <button onClick={() => setShowKeyInput(false)} className="cancel-btn">Ακύρωση</button>
          </div>
        )}
      </div>

      <div className="category-selector">
        <label>Επιλογή Κατηγορίας:</label>
        <select 
          value={selectedCategory} 
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-select"
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.name}>
              {cat.icon} {cat.name}
            </option>
          ))}
        </select>
      </div>

      <div className="config-section">
        <h3>📝 Προσαρμοσμένο Prompt</h3>
        <p className="section-description">
          Προσθέστε επιπλέον οδηγίες για το AI σχετικά με αυτή την κατηγορία
        </p>
        <textarea
          className="custom-prompt-input"
          value={config.customPrompt}
          onChange={(e) => setConfig({ ...config, customPrompt: e.target.value })}
          placeholder="π.χ. Για αυτή την κατηγορία, εστίασε στις τεχνολογίες React, Node.js και MongoDB. Πρότεινε πάντα modern best practices..."
          rows="6"
        />
        <div className="prompt-tips">
          <strong>💡 Συμβουλές:</strong>
          <ul>
            <li>Αναφέρετε τις κύριες τεχνολογίες της κατηγορίας</li>
            <li>Ορίστε το επίπεδο τεχνικότητας των απαντήσεων</li>
            <li>Προσθέστε συγκεκριμένα παραδείγματα ή case studies</li>
          </ul>
        </div>
      </div>

      <div className="config-section">
        <h3>❓ Προτεινόμενες Ερωτήσεις</h3>
        <p className="section-description">
          Ερωτήσεις που θα εμφανίζονται ως quick buttons στο chat
        </p>
        
        <div className="questions-list">
          {config.suggestedQuestions.map((question, index) => (
            <div key={index} className="question-item">
              <input
                type="text"
                value={question}
                onChange={(e) => updateQuestion(index, e.target.value)}
                placeholder={`Ερώτηση ${index + 1}`}
                className="question-input"
              />
              {config.suggestedQuestions.length > 1 && (
                <button
                  onClick={() => removeQuestion(index)}
                  className="remove-question-btn"
                  title="Αφαίρεση"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        <button onClick={addQuestion} className="add-question-btn">
          + Προσθήκη Ερώτησης
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="config-actions">
        <button 
          onClick={handleSave} 
          className="save-btn"
          disabled={loading}
        >
          {loading ? 'Αποθήκευση...' : '💾 Αποθήκευση Ρυθμίσεων'}
        </button>
      </div>

      <div className="config-preview">
        <h3>👁️ Προεπισκόπηση</h3>
        <div className="preview-box">
          <div className="preview-category">
            <strong>Κατηγορία:</strong> {selectedCategory}
          </div>
          {config.customPrompt && (
            <div className="preview-prompt">
              <strong>Custom Prompt:</strong>
              <p>{config.customPrompt}</p>
            </div>
          )}
          {config.suggestedQuestions.filter(q => q.trim()).length > 0 && (
            <div className="preview-questions">
              <strong>Προτεινόμενες Ερωτήσεις:</strong>
              <ul>
                {config.suggestedQuestions
                  .filter(q => q.trim())
                  .map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatConfigManager;
